import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wheat } from 'lucide-react';
import { tr } from '@/i18n/tr';
import { useAuth } from '@/auth/authContext';
import { setMyAccountActive } from '@/services/authService';
import { useGuardedExit } from '@/components/confirmDialog/unsavedContext';
import { useLogoutConfirm } from '@/components/confirmDialog/useLogoutConfirm';
import { TabBar, type TabId } from '@/components/tabBar/tabBar';
import { AlertIcon, ChatIcon, HomeIcon, PlusIcon, UserIcon } from '@/components/tabBar/tabBarIcons';
import styles from './appShell.module.scss';

export interface AppShellProps {
  /** Highlighted tab; undefined on non-tab routes (auth screens). */
  active?: TabId;
  onSelect: (tab: TabId) => void;
  chatBadge?: number;
  /** Answered-but-unopened reports; 0 hides it. */
  reportBadge?: number;
  /** Unread notifications. The bell lives inside Profil; 0 hides it. */
  profileBadge?: number;
  /**
   * Where to go when the centre FAB is pressed on the create screen, where it
   * turns into an x. Optional: without it the FAB keeps its plain plus, so the
   * cross never appears unless something is actually wired to it.
   */
  onExitCreate?: () => void;
  /** Detail screens hide the mobile tab bar (mockups show a contact footer instead). */
  showTabBar?: boolean;
  children: ReactNode;
}

const navItems: { id: TabId; label: string; icon: ReactNode }[] = [
  { id: 'explore', label: tr.nav.explore, icon: <HomeIcon /> },
  { id: 'chat', label: tr.nav.chat, icon: <ChatIcon /> },
  { id: 'createListing', label: tr.nav.createListing, icon: <PlusIcon /> },
  { id: 'report', label: tr.nav.report, icon: <AlertIcon /> },
  { id: 'profile', label: tr.nav.profile, icon: <UserIcon /> },
];

/**
 * Responsive app shell:
 * - mobile (< md): content + fixed bottom TabBar with center FAB
 * - desktop (>= md): fixed left sidebar, content takes the full remaining width
 * The breakpoint switch is pure CSS — both navs are rendered, one is display:none.
 */
/**
 * Desktop session footer at the bottom of the sidebar. The mobile counterpart
 * is the explore-header UserMenu — that whole header row is display:none at
 * desktop widths, so without this block a signed-in desktop user has no way
 * to log out. TEMPORARY: the profile module replaces it with the real entry.
 */
function SidebarSession() {
  const { status, user, logout } = useAuth();
  const navigate = useNavigate();
  const confirmLogout = useLogoutConfirm();

  if (status === 'loading') return null;

  if (status !== 'authed' || !user) {
    return (
      <div className={styles.session}>
        <button type="button" className={styles.sessionLogin} onClick={() => navigate('/login')}>
          {tr.auth.loginCta}
        </button>
      </div>
    );
  }

  const signOut = async () => {
    if (!(await confirmLogout())) return;
    await logout();
    navigate('/');
  };

  return (
    <div className={styles.session}>
      <span className={styles.sessionAvatar} aria-hidden>
        {user.name.trim().charAt(0).toLocaleUpperCase('tr')}
      </span>
      <span className={styles.sessionName}>{user.name}</span>
      <button type="button" className={styles.sessionLogout} onClick={signOut}>
        {tr.auth.menu.logout}
      </button>
    </div>
  );
}

/**
 * Persistent, not a toast: while the account is passive every write fails, and
 * the message explaining why must not disappear on a timer. It is also the way
 * back — a passive user can log in precisely so they can press this.
 */
function PassiveBanner() {
  const { status, user, refresh } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  if (status !== 'authed' || !user || user.isActive) return null;

  const activate = async () => {
    setBusy(true);
    try {
      await setMyAccountActive(true);
      await refresh();
    } catch {
      // The profile screen owns the error surface for this action; here the
      // banner simply stays put, which is already the honest signal.
      navigate('/profile');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.passiveBanner} role="status">
      <span>{tr.profile.status.banner}</span>
      <button type="button" onClick={() => void activate()} disabled={busy}>
        {tr.profile.status.bannerAction}
      </button>
    </div>
  );
}

export function AppShell({
  active,
  onSelect,
  onExitCreate,
  chatBadge = 0,
  reportBadge = 0,
  profileBadge = 0,
  showTabBar = true,
  children,
}: AppShellProps) {
  const guardExit = useGuardedExit();

  // Sidebar, tab bar and the centre FAB all route through here. Re-selecting the
  // tab you are already on is not an exit, so it skips the prompt.
  const handleSelect = async (tab: TabId) => {
    if (tab !== active && !(await guardExit())) return;
    onSelect(tab);
  };

  // The x on the create screen IS an exit, so it must ask before discarding
  // picked photos — and handleSelect cannot be reused for it, because that skips
  // the guard precisely when tab === active. Its own path, its own prompt.
  const exitCreate = async () => {
    if (!(await guardExit())) return;
    onExitCreate?.();
  };

  return (
    <div className={styles.shell}>
      <aside className={styles.sidebar}>
        <div className={styles.brand}>
          <span className={styles.brandIcon}>
            <Wheat />
          </span>
          <div>
            <strong className={styles.brandName}>{tr.app.name}</strong>
            <span className={styles.brandTagline}>{tr.app.tagline}</span>
          </div>
        </div>

        <nav className={styles.nav}>
          {navItems.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`${styles.navItem} ${active === item.id ? styles.navActive : ''}`}
              aria-current={active === item.id ? 'page' : undefined}
              data-tab={item.id}
              onClick={() => handleSelect(item.id)}
            >
              <span className={styles.navIcon}>
                {item.icon}
                {item.id === 'chat' && chatBadge > 0 && (
                  <span className={styles.badge}>{chatBadge}</span>
                )}
                {item.id === 'report' && reportBadge > 0 && (
                  <span className={styles.badge}>{reportBadge}</span>
                )}
                {item.id === 'profile' && profileBadge > 0 && (
                  <span className={styles.badge}>{profileBadge}</span>
                )}
              </span>
              {item.label}
            </button>
          ))}
        </nav>

        <SidebarSession />
      </aside>

      <main className={`${styles.content} ${showTabBar ? '' : styles.noTabBar}`}>
        <PassiveBanner />
        {children}
      </main>

      {showTabBar && (
        <TabBar
          active={active}
          onSelect={handleSelect}
          onCloseCreate={onExitCreate && (() => void exitCreate())}
          chatBadge={chatBadge}
          reportBadge={reportBadge}
          profileBadge={profileBadge}
        />
      )}
    </div>
  );
}
